import { useMemo } from 'react'
import type { TripPosition } from '../api'
import { formatElevation, type UnitSystem } from '../preferences'
import { createTripTimeline } from './tripTimeline'

interface ElevationSample {
  altitude: number
  progress: number
}

function elevationAtProgress(samples: ElevationSample[], progress: number): number {
  const first = samples[0]!
  const last = samples.at(-1)!
  if (progress <= first.progress) return first.altitude
  if (progress >= last.progress) return last.altitude
  let low = 1
  let high = samples.length - 1
  while (low < high) {
    const middle = Math.floor((low + high) / 2)
    if (samples[middle]!.progress < progress) low = middle + 1
    else high = middle
  }
  const nextIndex = low
  const before = samples[nextIndex - 1]!
  const after = samples[nextIndex]!
  const interval = Math.max(Number.EPSILON, after.progress - before.progress)
  const fraction = (progress - before.progress) / interval
  return before.altitude + (after.altitude - before.altitude) * fraction
}

export function elevationAtTripProgress(positions: TripPosition[], progress: number): number | undefined {
  const timeline = createTripTimeline(positions)
  const samples = positions
    .map((position, index) => ({
      altitude: position.altitudeMeters,
      progress: timeline.progressAtPositionIndex(index),
    }))
    .filter((sample): sample is ElevationSample => sample.altitude !== undefined)
  if (!samples.length) return undefined
  if (samples.length === 1) return samples[0]!.altitude
  return elevationAtProgress(samples, progress)
}

export function ElevationProfile({
  positions,
  progress,
  unitSystem,
}: {
  positions: TripPosition[]
  progress: number
  unitSystem: UnitSystem
}) {
  const profile = useMemo(() => {
    const timeline = createTripTimeline(positions)
    const altitudeSamples = positions
      .map((position, index) => ({
        altitude: position.altitudeMeters,
        progress: timeline.progressAtPositionIndex(index),
      }))
      .filter((sample): sample is ElevationSample => sample.altitude !== undefined)
    if (altitudeSamples.length < 2) return null

    const step = Math.max(1, Math.ceil(altitudeSamples.length / 120))
    const sampled = altitudeSamples.filter((_, index) => index % step === 0)
    if (sampled.at(-1) !== altitudeSamples.at(-1)) sampled.push(altitudeSamples.at(-1)!)
    const min = Math.min(...sampled.map((sample) => sample.altitude))
    const max = Math.max(...sampled.map((sample) => sample.altitude))
    const span = Math.max(1, max - min)
    const point = (sample: ElevationSample) => {
      const x = sample.progress * 320
      const y = 82 - ((sample.altitude - min) / span) * 70
      return `${x.toFixed(1)},${y.toFixed(1)}`
    }
    return { altitudeSamples, max, min, points: sampled.map(point).join(' '), sampled, span }
  }, [positions])
  if (!profile) return null

  const { altitudeSamples, max, min, points, sampled, span } = profile
  const point = (sample: ElevationSample) => {
    const x = sample.progress * 320
    const y = 82 - ((sample.altitude - min) / span) * 70
    return `${x.toFixed(1)},${y.toFixed(1)}`
  }
  const currentAltitude = elevationAtProgress(altitudeSamples, progress)
  const currentSample = { altitude: currentAltitude, progress }
  const elapsedPoints = [
    ...sampled.filter((sample) => sample.progress < progress),
    currentSample,
  ].map(point).join(' ')
  const currentX = progress * 320
  const currentY = 82 - ((currentAltitude - min) / span) * 70

  return (
    <div className="elevation-profile-block">
      <div
        className="elevation-profile"
        role="img"
        aria-label={`Elevation profile from ${formatElevation(min, unitSystem)} to ${formatElevation(max, unitSystem)}, current elevation ${formatElevation(currentAltitude, unitSystem)}`}
        data-testid="elevation-progress"
        data-progress={Math.round(progress * 1000)}
      >
        <svg viewBox="0 0 320 92" preserveAspectRatio="none" aria-hidden="true">
          <polyline className="elevation-profile__route" points={points} />
          <polyline className="elevation-profile__elapsed" points={elapsedPoints} />
          <line className="elevation-profile__cursor" x1={currentX} y1="8" x2={currentX} y2="86" />
          <circle className="elevation-profile__point" cx={currentX} cy={currentY} r="4" />
        </svg>
      </div>
      <dl className="trip-elevation__stats">
        <div><dt>Lowest</dt><dd data-testid="elevation-lowest">{formatElevation(min, unitSystem)}</dd></div>
        <div><dt>Highest</dt><dd data-testid="elevation-highest">{formatElevation(max, unitSystem)}</dd></div>
      </dl>
    </div>
  )
}
